import { Form } from '@/components/form';
import { useForm } from 'react-hook-form';
import { OTHER_CHECKBOX_VALUE, QuestionRenderer } from './question-renderer/question-renderer';
import styles from './variable-question-form.module.css';
import { useTranslation } from '@/contexts/i18n-context';
import {
  AnswerType,
  GetQuestionsByPageResponse,
  GetVQResponsesResponse,
  Question,
  QuestionResponse,
} from '@/api/api.schemas';
import { z } from 'zod';
import {
  isPastDate,
  optionalPastDate,
  optionalString,
  requiredPastDate,
  requiredString,
  zt,
} from '@/util/validators';
import { photoSchema } from '@/components/uploads/photo-upload/photo-upload';
import { MAX_PHOTO_COUNT, MIN_PHOTO_COUNT } from '@/util/constants';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { Section } from '@/components/containers/section';
import { Banner } from '@/components/banner';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import { createEditUrl } from '@/util/helpers';
import { useEncounter } from '@/contexts/encounter-context';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useUpdateEncountersResponses } from '@/api/update-vq-responses';
import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageContainer } from '@/components/containers/page-container';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { useSaveProgressProps } from '@/hooks/use-save-progress-props';
import { PostSubmitPolicy, usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';
import { MarketingSpot } from '@/components/marketing-spot';
import {
  getResponseName,
  getResponseOtherName,
  isQuestionTriggered,
  parseResponse,
} from './variable-question-utils';

const translationBase = 'visit.condition';
const photoTranslationBase = 'visit.photoUpload';

interface VariableQuestionsFormProps {
  questionsData: GetQuestionsByPageResponse;
  responsesData: GetVQResponsesResponse;
  onSubmit: () => void;
}

export function VariableQuestionsForm({
  questionsData,
  responsesData,
  onSubmit,
}: VariableQuestionsFormProps) {
  const { encounter, invalidateEncounter } = useEncounter();
  const isMobile = useMediaQuery('--screen-md');
  const { nextStep } = useVisitNavigation();
  const { t } = useTranslation();
  const { navigatePostSubmit } = usePostSubmitNavigation();

  const prevPageId = questionsData.prevPageId;

  const combinedPageIds = responsesData?.variableResponses
    .filter((response) => response.reviewTitle === questionsData.page.reviewTitle)
    .map((response) => response.pageId);

  const showNextButtonOnReview =
    !!questionsData.nextPageId && combinedPageIds.includes(questionsData.nextPageId);
  const showBackButtonOnReview = !!prevPageId && combinedPageIds.includes(prevPageId);

  const { mutate: saveResponses, isPending: isSavingResponses } = useUpdateEncountersResponses();

  const dynamicSchema = createDynamicSchema(questionsData.page.questions);

  type DynamicFormValues = z.infer<typeof dynamicSchema>;

  const defaultValues: DynamicFormValues = useMemo(() => {
    const values: DynamicFormValues = {};

    questionsData.page.questions.forEach((question) => {
      const name = getResponseName(question.questionId);

      values[name] = undefined;

      // Photo and file uploads are excluded from defaultValues because they are stored separately
      // from the rest of the responses. Photos are stored in the Files table and blob storage, and are
      // loaded independently via the PhotoUpload component using the encounters API.
      // Photo submissions are also handled separately in onSubmit using handleCreate
      // and handleUpdate functions that make dedicated API calls rather than being
      // saved as part of the variable question responses.
      if (
        question.answerType === AnswerType.photoUpload ||
        question.answerType === AnswerType.file
      ) {
        return;
      }

      const response = responsesData.variableResponses.find(
        (response) => response.questionId === question.questionId,
      );
      if (response) {
        values[name] = parseResponse(response, question);
        if (response.responseOther != null) {
          values[getResponseOtherName(question.questionId)] = response.responseOther;
        }
      } else if (question.answerType === AnswerType.radio) {
        values[name] = question.answers[0].answerId.toString();
      }
    });
    return values;
    // Default values should not be changing. If they do - we need to reset the form.
    //  If you ever need this to run more than once - make sure you think about the rerenders
    // of reach-hook-form and how it works.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm<DynamicFormValues>({
    resolver: zodResolver(dynamicSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues,
  });

  const onSubmitHandler = (data: DynamicFormValues, isIncomplete: boolean) => {
    const responses: (QuestionResponse | undefined)[] = questionsData.page.questions.map(
      (question) => {
        const isMultipleType = question.answerType === AnswerType.checkboxOther;
        const fieldValue = data[getResponseName(question.questionId)];

        if (
          question.answerType === AnswerType.photoUpload ||
          question.answerType === AnswerType.file
        ) {
          return {
            type: 'Single',
            questionId: question.questionId,
            response: fieldValue?.toString() ?? '',
          };
        }

        if (!fieldValue && (question.isRequired || !!question.triggeredByAnswerChoiceId)) {
          return undefined;
        }

        if (!!question.triggeredByAnswerChoiceId && !isQuestionTriggered(data, question)) {
          return {
            type: isMultipleType ? 'Multiple' : 'Single',
            questionId: question.questionId,
            response: undefined,
          };
        }

        return {
          type: isMultipleType ? 'Multiple' : 'Single',
          questionId: question.questionId,
          response: isMultipleType ? fieldValue : fieldValue.toString(),
          responseOther: data[getResponseOtherName(question.questionId)]?.toString(),
        };
      },
    );
    const filteredResponses = responses.filter((response) => response !== undefined);
    saveResponses(
      {
        encounterId: encounter.id,
        data: {
          answers: filteredResponses,
          incomplete: isIncomplete,
        },
      },
      {
        onSuccess: () => {
          invalidateEncounter();
          onSubmit();

          const policy: PostSubmitPolicy = {};
          if (showNextButtonOnReview) {
            policy.resolveEdit = ({ editMode }) => ({
              path: createEditUrl(
                `/visit/${encounter.id}/questions/${questionsData.nextPageId}`,
                editMode,
              ),
            });
          }
          navigatePostSubmit({ isIncomplete, policy });
        },
      },
    );
  };

  const saveProgressProps = useSaveProgressProps(form, onSubmitHandler);

  const isLastPage = !questionsData.nextPageId;

  return (
    <PageContainer>
      {!isMobile && (
        <VisitTitleButtons
          saveProgress={saveProgressProps}
          showNextButtonOnReview={showNextButtonOnReview}
          showBackButtonOnReview={showBackButtonOnReview}
        />
      )}
      <GradientFormTitle>
        {isMobile && (
          <VisitTitleButtons
            showNextButtonOnReview={showNextButtonOnReview}
            showBackButtonOnReview={showBackButtonOnReview}
          />
        )}
        <VisitNavigation />
        <h1 className="h3">{questionsData.page.title}</h1>
        {questionsData.page.subtitle && (
          <p className={styles.subtitle}>{questionsData.page.subtitle}</p>
        )}
        {isMobile && <SaveProgressButton {...saveProgressProps} />}
      </GradientFormTitle>
      {isLastPage && (
        <MarketingSpot page="aboutYourCondition" spot="top" className={styles.marketingContent} />
      )}
      <Form form={form} onSubmit={(data) => onSubmitHandler(data, false)}>
        <Section className={styles.formContainer}>
          {!prevPageId && (
            <Banner type="warning" label={t(`${translationBase}.oneConditionPerVisitBanner`)} />
          )}
          <div className={styles.questionContainer}>
            {questionsData.page.questions.map((question) => (
              <QuestionRenderer
                form={form}
                questions={questionsData.page.questions}
                question={question}
                name={getResponseName(question.questionId)}
                otherName={getResponseOtherName(question.questionId)}
                key={question.questionId}
              />
            ))}
          </div>
        </Section>
        <VisitFormButtonRow
          nextStep={nextStep?.name}
          buttonTextKey="common.next"
          type="submit"
          rightIcon="arrow_forward"
          loading={isSavingResponses}
          showNextButtonOnReview={showNextButtonOnReview}
        />
      </Form>
    </PageContainer>
  );
}

function createDynamicSchema(questions: Question[]) {
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  questions.forEach((question) => {
    const fieldName = getResponseName(question.questionId);

    const isOptional = !question.isRequired || !!question.triggeredByAnswerChoiceId;

    const schema = getFieldSchema(question, isOptional);
    if (schema) {
      schemaFields[fieldName] = schema;
    }

    if (question.answerType === AnswerType.checkboxOther) {
      schemaFields[getResponseOtherName(question.questionId)] = optionalString(question.maxLength);
    }
  });

  const baseSchema = z.object(schemaFields);

  return baseSchema.superRefine((data, ctx) => {
    questions.forEach((question) => {
      validateTriggeredQuestion(question, data, ctx);

      if (question.answerType === AnswerType.checkboxOther) {
        const fieldName = getResponseName(question.questionId);
        const otherFieldName = getResponseOtherName(question.questionId);
        const checkboxValues = data[fieldName] as string[];
        const otherValue = data[otherFieldName] as string;

        if (checkboxValues?.includes(OTHER_CHECKBOX_VALUE) && !otherValue?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: zt('common.errors.required'),
            path: [otherFieldName],
          });
        }
      }
    });
  });
}

function validateTriggeredQuestion(
  question: Question,
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
) {
  if (!question.isRequired || !isQuestionTriggered(data, question)) {
    return;
  }

  const fieldName = getResponseName(question.questionId);
  const value = data[fieldName];

  const schema = getFieldSchema(question, false);
  if (!schema) return;

  const result = schema.safeParse(value);
  if (!result.success) {
    result.error.issues.forEach((issue) => {
      ctx.addIssue({
        ...issue,
        path: [fieldName, ...issue.path],
      });
    });
  }
}

function getFieldSchema(question: Question, isOptional: boolean): z.ZodTypeAny | null {
  switch (question.answerType) {
    case AnswerType.yesNo:
      return isOptional
        ? z.enum(['yes', 'no']).nullable()
        : z
            .enum(['yes', 'no'])
            .nullable()
            .refine((val) => val, { message: zt('common.errors.required') });
    case AnswerType.radio:
    case AnswerType.textShort:
      return isOptional ? z.string().optional() : requiredString();
    case AnswerType.textLong:
      return isOptional
        ? optionalString(question.maxLength)
        : requiredString(undefined, question.maxLength);
    case AnswerType.datePast:
      return isOptional ? optionalPastDate() : requiredPastDate();
    case AnswerType.datePastWithOption:
      return datePastWithOptionSchema(question, isOptional);
    case AnswerType.dropdown:
      return isOptional
        ? z.string().optional()
        : requiredString(zt('common.errors.notSelectedOption'));
    case AnswerType.bodyLocation:
      return isOptional
        ? z.string().optional()
        : requiredString(zt(`${translationBase}.bodyLocationError`));
    case AnswerType.checkboxOther:
      return isOptional
        ? z.string().array().default([])
        : z
            .string()
            .array()
            .min(1, { message: zt('common.errors.required') })
            .default([]);
    case AnswerType.slider10:
      return z.number().min(0, zt('common.errors.required'));
    case AnswerType.photoUpload:
      return z
        .array(photoSchema)
        .min(MIN_PHOTO_COUNT, zt(`${photoTranslationBase}.error.minPhotos`))
        .max(MAX_PHOTO_COUNT, zt(`${photoTranslationBase}.error.maxPhotos`))
        .refine((photos) => photos.every((photo) => photo.bodyLocation), {
          message: zt(`${photoTranslationBase}.error.noBodyLocation`),
          path: [],
        });
    case AnswerType.file:
      return null;
    default:
      return null;
  }
}

function datePastWithOptionSchema(question: Question, isOptional: boolean) {
  const optionIds = question.answers.map((answer) => answer.answerId.toString());
  const isValidValue = (value: string) => optionIds.includes(value) || isPastDate(value);

  if (isOptional) {
    return z
      .string()
      .optional()
      .refine((val) => !val || isValidValue(val), {
        message: zt('common.errors.pastDate'),
      });
  }

  return requiredString().refine(isValidValue, { message: zt('common.errors.pastDate') });
}
